import React, { useCallback, useContext, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import Sidebar from "../Sidebar";
import MobileTopBar from "../MobileTopBar";
import NotificationsPanel from "../NotificationsPanel";
import { useAuth } from "../../../context/AuthContext";
import { usePendingContactCount } from "../../../hooks/queries/useContactRequests";
import { useUnreadMessageCount } from "../../../hooks/queries/useDirectMessages";
import { useNotificationStream } from "../../../hooks/queries/useNotifications";
import { GuardianAdvisoryContext } from "./guardianAdvisory";
import { savedSearchAlertPath } from "../../../utils/notificationLinks";

const LoadingScreen = () => (
  <div className="flex h-screen items-center justify-center bg-gray-50">
    <div className="text-center">
      <h2 className="text-xl font-semibold text-blue-600 mb-2">
        Conectando con Athletain...
      </h2>
      <p className="text-gray-500 text-sm">Cargando tu información...</p>
    </div>
  </div>
);

const NotificationsContext = React.createContext({
  openNotifications: () => {},
  pendingContactCount: 0,
  refreshPendingContactCount: () => {},
});

export const useNotifications = () => React.useContext(NotificationsContext);

const AppShell = () => {
  const { userRole, isLoading, signOut, authUserId } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  // Under the guardian notice the shell fills the space left below it.
  const belowGuardianNotice = useContext(GuardianAdvisoryContext);

  // Badge count of pending contact requests for the athlete's sidebar. Only
  // athletes have an inbox; scouts always see zero.
  const pendingCountQuery = usePendingContactCount({
    enabled: userRole === "athlete",
  });
  const pendingContactCount =
    userRole === "athlete" ? (pendingCountQuery.data ?? 0) : 0;

  const unreadMsgQuery = useUnreadMessageCount();
  const unreadMessageCount = unreadMsgQuery.data ?? 0;

  const refreshPendingContactCount = useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: ["contact-requests", "pending-count"],
      }),
    [queryClient],
  );

  // Live notification delivery (Flujo Contacto #1). Mounted here because the
  // shell is the one component that exists for the whole authenticated session:
  // one stream per session, not one per screen. Must sit above the isLoading
  // return so the hook order stays stable across renders.
  useNotificationStream(Boolean(authUserId));

  if (isLoading) {
    return <LoadingScreen />;
  }

  const openNotifications = () => setIsNotificationsOpen(true);

  // Contact-request notifications carry a contact_request_id. Once a request is
  // approved (by the athlete, or by a minor's guardian) both sides go straight
  // into the chat; otherwise the athlete goes to their inbox and the scout back
  // to their athlete list to see the updated status.
  const handleNotificationClick = (notification) => {
    // Weekly new-talent notice: open that saved search's results.
    const searchPath = savedSearchAlertPath(notification);
    if (searchPath) {
      setIsNotificationsOpen(false);
      navigate(searchPath);
      return;
    }
    // Academy membership: the coach reviews join requests in "Mis deportistas";
    // the athlete sees their membership in Configuración.
    if (notification?.metadata?.academy_athlete_id) {
      setIsNotificationsOpen(false);
      navigate(userRole === "scout" ? "/scout/my-athletes" : "/athlete/settings");
      return;
    }
    const crId = notification?.metadata?.contact_request_id;
    if (!crId) return;
    setIsNotificationsOpen(false);
    const approved = notification.metadata.decision === "approved";
    if (notification.type === "message" || (userRole === "athlete" && approved)) {
      const base = userRole === "athlete" ? "/athlete/messages" : "/scout/messages";
      navigate(`${base}/${crId}`);
    } else if (userRole === "athlete") {
      navigate("/athlete/contact-requests");
    } else if (userRole === "scout") {
      navigate(
        notification.metadata.decision === "approved"
          ? `/scout/messages/${crId}`
          : "/scout/athletes",
      );
    }
  };
  const goToProfile = () =>
    navigate(userRole === "athlete" ? "/athlete/profile/personal" : "/scout/profile");
  // Scouts have a dedicated search surface; athletes don't, so the icon is hidden for them.
  const onSearchClick =
    userRole === "scout" ? () => navigate("/scout/searches") : undefined;

  return (
    <NotificationsContext.Provider
      value={{ openNotifications, pendingContactCount, refreshPendingContactCount }}
    >
      {/* `print-shell` releases the h-screen/overflow-hidden lock when printing:
          without it a report exports as a single clipped viewport instead of
          flowing across pages. See the print block in index.css. */}
      <div className={`print-shell flex ${belowGuardianNotice ? "h-full" : "h-screen"} bg-gray-50`}>
        {/* Desktop: persistent column. Hidden below lg. */}
        <Sidebar
          className="no-print"
          variant="static"
          userRole={userRole}
          onLogout={signOut}
          pendingContactCount={pendingContactCount}
          unreadMessageCount={unreadMessageCount}
        />

        {/* Mobile: slide-in drawer overlay. */}
        <Sidebar
          className="no-print"
          variant="drawer"
          userRole={userRole}
          onLogout={signOut}
          isOpen={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
          onNavigate={() => setIsDrawerOpen(false)}
          pendingContactCount={pendingContactCount}
          unreadMessageCount={unreadMessageCount}
        />

        <div className="print-shell flex min-w-0 flex-1 flex-col overflow-hidden">
          <MobileTopBar
            className="no-print lg:hidden"
            onMenuClick={() => setIsDrawerOpen(true)}
            onSearchClick={onSearchClick}
            onNotificationsClick={openNotifications}
            onProfileClick={goToProfile}
          />
          <Outlet />
        </div>

        <NotificationsPanel
          isOpen={isNotificationsOpen}
          onClose={() => setIsNotificationsOpen(false)}
          userId={authUserId}
          onNotificationClick={handleNotificationClick}
        />
      </div>
    </NotificationsContext.Provider>
  );
};

export default AppShell;
